import React, { ReactNode, useEffect } from 'react';

/**
 * Opera PMS-style floating window. Used for all modal/detail screens
 * to give Mum the visual continuity she's used to.
 *
 * Two sizes:
 *   - "large":   full layout with side button column + bottom action strip
 *                (used by stay detail, profile editor, etc)
 *   - "compact": no action strip, smaller width, lighter feel
 *                (used by block-room, simple confirm dialogs)
 */

export interface SideButton {
  label: string;
  onClick?: () => void;
  primary?: boolean;
  danger?: boolean;
  disabled?: boolean;
}

export interface ActionStripTab {
  label: string;
  onClick?: () => void;
  enabled?: boolean;
  active?: boolean;
}

interface OperaWindowProps {
  title: string;
  subtitle?: string;         // record name next to the title (e.g. the profile's working name)
  status?: string;           // shown in red in title bar (e.g. "CHECKED IN")
  recordId?: string;         // optional small grey text in title bar
  size?: 'large' | 'compact';
  onClose: () => void;
  sideButtons?: SideButton[];
  actionStrip?: ActionStripTab[];
  children: ReactNode;
}

export function OperaWindow({
  title, subtitle, status, recordId,
  size = 'large',
  onClose, sideButtons, actionStrip,
  children,
}: OperaWindowProps) {
  // Escape closes the window
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const isCompact = size === 'compact';

  return (
    <div className="opera-overlay" onClick={onClose}>
      <div
        className={`opera-window ${isCompact ? 'compact' : 'large'}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Title bar */}
        <div className="opera-titlebar">
          <div className="opera-title-text">
            <span className="opera-title-main">{title}</span>
            {subtitle && <span className="opera-title-sub"> — {subtitle}</span>}
            {recordId && <span className="opera-title-id"> · {recordId}</span>}
            {status && <span className="opera-title-status">{status}</span>}
          </div>
          <div className="opera-title-controls">
            <span className="winbtn">_</span>
            <span className="winbtn">□</span>
            <button className="winbtn close" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>

        {/* Body: form area on left, side buttons on right */}
        <div className="opera-body">
          <div className="opera-form-area">{children}</div>
          {sideButtons && sideButtons.length > 0 && (
            <div className="opera-side-buttons">
              {sideButtons.map((b, i) => (
                <button
                  key={i}
                  className={`opera-side-btn ${b.primary ? 'primary' : ''} ${b.danger ? 'danger' : ''}`}
                  onClick={b.onClick}
                  disabled={b.disabled}
                >
                  {b.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Bottom action strip (Opera's red-tabbed row) */}
        {!isCompact && actionStrip && actionStrip.length > 0 && (
          <div className="opera-action-strip">
            {actionStrip.map((tab, i) => (
              <button
                key={i}
                className={`opera-strip-tab ${tab.enabled === false ? 'disabled' : ''} ${tab.active ? 'active' : ''}`}
                onClick={tab.enabled !== false ? tab.onClick : undefined}
                disabled={tab.enabled === false}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Form section box — used to wrap related fields inside an OperaWindow.
 * Renders as a labelled bordered box, like Opera's "Address Information" /
 * "Internal Information" panels.
 */
interface OperaSectionProps {
  title?: string;
  children: ReactNode;
}
export function OperaSection({ title, children }: OperaSectionProps) {
  return (
    <fieldset className="opera-section">
      {title && <legend className="opera-section-legend">{title}</legend>}
      <div className="opera-section-body">{children}</div>
    </fieldset>
  );
}

/**
 * Form row with a fixed-width label and a field. Mimics Opera's
 * dense label-on-left layout.
 */
interface OperaFieldProps {
  label: string;
  required?: boolean;
  hint?: string;            // small grey text to the right of the field
  status?: string;          // small red text (like "Sunday" / "Wednesday")
  children: ReactNode;
}
export function OperaField({ label, required, hint, status, children }: OperaFieldProps) {
  return (
    <div className="opera-field">
      <label className="opera-field-label">
        {label}{required && <span className="opera-required">*</span>}
      </label>
      <div className="opera-field-input">{children}</div>
      {status && <span className="opera-field-status">{status}</span>}
      {hint && <span className="opera-field-hint">{hint}</span>}
    </div>
  );
}
